import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { CaretDownIcon as CaretDown, CheckIcon as Check, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";

// options: [{ value, label, color? }]  value: string[]  onChange(next: string[])
// searchable: mostra campo di ricerca + "Seleziona tutti/Pulisci" (per liste lunghe: Cliente, Contratto)
export function MultiSelect({ options, value, onChange, placeholder = "Tutti", width = "w-52", testid, searchable = false }) {
  const [q, setQ] = useState("");
  const sel = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  const selected = options.filter((o) => sel.includes(o.value));
  const label = selected.length === 0 ? placeholder
    : selected.length <= 2 ? selected.map((o) => o.label).join(", ")
    : `${selected.length} selezionati`;
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => (o.label || "").toLowerCase().includes(s)) : options;
  }, [options, q]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`${width} justify-between rounded-sm bg-white font-normal`} data-testid={testid}>
          <span className="truncate">{label}</span>
          <CaretDown size={14} className="shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1 rounded-sm" align="start">
        {searchable && (
          <div className="relative mb-1">
            <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="h-8 rounded-sm pl-7 text-sm" data-testid={`${testid}-search`} />
          </div>
        )}
        {searchable && (
          <div className="flex items-center justify-between px-1 pb-1 mb-1 border-b border-border text-xs">
            <button type="button" className="text-primary hover:underline" onClick={() => onChange(Array.from(new Set([...sel, ...filtered.map((o) => o.value)])))} data-testid={`${testid}-selectall`}>Seleziona tutti</button>
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => onChange([])} data-testid={`${testid}-pulisci`}>Pulisci</button>
          </div>
        )}
        <div className="max-h-64 overflow-auto">
          {filtered.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">Nessuna opzione</div>}
          {filtered.map((o) => {
            const checked = sel.includes(o.value);
            return (
              <div key={o.value} role="button" tabIndex={0} onClick={() => toggle(o.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(o.value); } }} data-testid={`${testid}-opt-${o.value}`}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-secondary text-sm text-left cursor-pointer">
                <Checkbox checked={checked} className="pointer-events-none" />
                {o.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: o.color }} />}
                <span className="flex-1 truncate">{o.label}</span>
                {checked && <Check size={13} className="text-primary shrink-0" />}
              </div>
            );
          })}
        </div>
        {!searchable && sel.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="w-full mt-1 px-2 py-1.5 text-xs text-muted-foreground hover:bg-secondary rounded-sm text-left border-t border-border" data-testid={`${testid}-clear`}>
            Deseleziona tutto
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
